import {
  Metadata,
  MetadataData,
} from "@metaplex-foundation/mpl-token-metadata";
import { encode } from "@project-serum/anchor/dist/cjs/utils/bytes/bs58";
import { AnchorWallet, WalletContextState } from "@solana/wallet-adapter-react";
import {
  LAMPORTS_PER_SOL,
  PublicKey,
  SimulatedTransactionResponse,
} from "@solana/web3.js";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { chunk } from "lodash";
import { divide, multiply, pow } from "mathjs";
import { getAllClubMembers } from "../api/clubs.api";
import { postQuickNode } from "../api/utilities/http";
import {
  CLUB_NAME_MAX_LENGTH,
  SOCIAL_NETWORK_ICONS,
  SOCIAL_NETWORKS,
} from "../common/constants/clubs-wizard.constants";
import {
  EMPTY_STRING,
  GOVERNANCE_ERROR_IDENTIFIER,
  MESSAGE_TYPE,
  TRANSACTION_ERROR_IDENTIFIER,
  TRANSFER_ERROR_IDENTIFIER,
} from "../common/constants/common.constants";
import { ClubType, TreasuryFundraiseStatus } from "../common/enums/club.enum";
import { Role, SocialNetworks, UserRole } from "../common/enums/common.enums";
import { WhitelistingStatus } from "../common/enums/whitelisting.enums";
import {
  GetUserDetailsDto,
  IClubBasicInfo,
  IClubMember,
  IClubMemberAndInvitation,
  IClubTreasury,
  ITreasuryTokenAccInfo,
  IUpdateClubDto,
  IUpdateClubFormValues,
  TransactionError,
} from "../common/interfaces/clubs.interface";
import {
  IDetailNftData,
  IFormField,
  IOption,
  ISocialNetwork,
} from "../common/interfaces/common.interface";
import { IProposalWithMetaData } from "../common/interfaces/proposals.interface";
import {
  IUpdateUserAccountDto,
  IUserAccount,
  ISaveWhitelistingDataDto,
} from "../common/interfaces/user.interface";
import { transferToSelf } from "../solana/common/helpers/common.helpers";
import { getNftTokenAccountsByOwner } from "../solana/common/helpers/nftClubs.helpers";
import {
  governanceSeed,
  METADATA_PROGRAM_ID,
  metadataSeed,
  monkeMint,
  RPC_CONNECTION,
  splGovernanceProgram,
} from "../solana/common/utilities";
import {
  fetchAllClubsFromSolana,
  getClubMembers,
  getClubMembersAndInvitations,
} from "../solana/methods/clubs";

import { createNotification } from "./notification";

export const checkSocialNetworkUrl = (
  socialNetwork: SocialNetworks,
  value: string
) => {
  let regex;
  switch (socialNetwork) {
    case SocialNetworks.telegram: {
      regex = new RegExp(
        `(https?:\/\/)?(www[.])?(telegram|t)\.me\/([a-zA-Z0-9_-]*)\/?$`
      );
      break;
    }
    case SocialNetworks.discord: {
      regex = new RegExp(
        `((https?:\/\/)?(discord\.com)\/invite\/+[a-zA-Z0-9_-])|((https?:\/\/)?(discord\.gg)\/+[a-zA-Z0-9_-])`
      );
      break;
    }
    case SocialNetworks.reddit: {
      regex = new RegExp(
        `(https?:\/\/)?(www\.)?(reddit\.com\/r)\/+[a-zA-Z0-9]`
      );
      break;
    }
    case SocialNetworks.tiktok: {
      regex = new RegExp(`(https?:\/\/)?(www\.)?(tiktok\.com)\/@+[a-zA-Z0-9]`);
      break;
    }
    case SocialNetworks.instagram: {
      regex = new RegExp(
        `(https?:\/\/)?(www\.)?(instagram\.com)\/+[a-zA-Z0-9]`
      );
      break;
    }
    case SocialNetworks.twitter: {
      regex = new RegExp(`(https?:\/\/)?(www\.)?(twitter\.com)\/+[a-zA-Z0-9]`);
      break;
    }
    default:
      regex = new RegExp(`${SocialNetworks[socialNetwork]}.com\/(\\w+)`);
  }
  return regex.test(value);
};

export const checkEmailLink = (value: string) => {
  const regex = new RegExp("\\w+@\\w+\\.\\w+");
  return regex.test(value);
};

export const mapSocialsToWizardFields = (
  formFields: any
): {
  network: IFormField;
  active: boolean;
}[] => {
  return [
    {
      network: formFields.discord,
      active: true,
    },
    {
      network: formFields.twitter,
      active: false,
    },
    {
      network: formFields.telegram,
      active: false,
    },
    {
      network: formFields.linkedin,
      active: false,
    },
    {
      network: formFields.reddit,
      active: false,
    },
    {
      network: formFields.instagram,
      active: false,
    },
    {
      network: formFields.tiktok,
      active: false,
    },
  ];
};

export const setInitialSocialNetwoksValues = (setFieldValue) => {
  SOCIAL_NETWORKS.map((key) => {
    setFieldValue(key, null);
  });
};

export const validateSocialNetworks = (
  errors,
  values,
  skipDiscordValidation?: boolean
) => {
  if (
    !checkSocialNetworkUrl(SocialNetworks.linkedin, values.linkedin) &&
    values.linkedin !== ""
  ) {
    errors.linkedin = "Link is not valid";
  }
  if (
    !checkSocialNetworkUrl(SocialNetworks.telegram, values.telegram) &&
    values.telegram !== EMPTY_STRING
  ) {
    errors.telegram = "Please enter valid telegram link.";
  }
  if (
    values.reddit !== EMPTY_STRING &&
    !checkSocialNetworkUrl(SocialNetworks.reddit, values.reddit)
  ) {
    errors.reddit = "Please enter valid reddit group link.";
  }
  if (
    values.tiktok !== EMPTY_STRING &&
    !checkSocialNetworkUrl(SocialNetworks.tiktok, values.tiktok)
  ) {
    errors.tiktok = "Please enter valid tiktok link.";
  }
  if (
    values.instagram !== EMPTY_STRING &&
    !checkSocialNetworkUrl(SocialNetworks.instagram, values.instagram)
  ) {
    errors.instagram = "Please enter valid instagram link.";
  }
  if (
    values.twitter !== EMPTY_STRING &&
    typeof values.twitter !== "string"
    // todo should we delete this?
    // !checkSocialNetworkUrl(SocialNetworks.twitter, values.twitter)
  ) {
    errors.twitter = "Please enter valid twitter link.";
  }
};

export const validateBasicClubInfo = (errors, values) => {
  if (!validateAlphanumericString(values.clubName)) {
    errors.clubName = "Club name cannot contain special characters!";
  }
  if (values.clubName === EMPTY_STRING) {
    errors.clubName = "Club name is required field";
  }
  if (
    values.clubName !== EMPTY_STRING &&
    values.clubName.length > CLUB_NAME_MAX_LENGTH
  ) {
    errors.clubName = "Club name is too long. Max length is 32 characters.";
  }
  if (values.clubDescription === EMPTY_STRING) {
    errors.clubDescription = "Club description is required field";
  }
  if (values.clubEmail === EMPTY_STRING) {
    errors.clubEmail = "Club email is required field";
  }
  if (!checkEmailLink(values.clubEmail) && values.clubEmail !== EMPTY_STRING) {
    errors.clubEmail = "Club email is not valid";
  }
  if (values.isClubNameTaken) {
    errors.clubName = "Club name is already taken.";
  }
  if (values.clubTags.length === 21) {
    errors.clubTags = "Maximum of 20 tags is allowed!";
  }
  if (
    values.clubTags.length > 0 &&
    !validateTagValue(values.clubTags[values.clubTags.length - 1].label)
  ) {
    errors.clubTags = "Tags can only contain alphanumeric characters!";
  }
};

export function getTrimmedPublicKey(publicKey: PublicKey | string): string {
  const publicKeyString = publicKey.toString();
  return (
    publicKeyString.substring(0, 5) +
    "..." +
    publicKeyString.substring(publicKeyString.length - 5)
  );
}

export function validateStartAndEndDate(
  startDate: Date,
  endDate: Date,
  startDateNameField: string,
  endDateNameField: string
) {
  const errors: any = {};
  const currentDate = new Date().setHours(0, 0, 0, 0);
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (start.getTime() < currentDate) {
    errors[startDateNameField] = "Start date can't be before today!";
  }
  if (end.getTime() <= startDate.getTime()) {
    errors[endDateNameField] = "End date must be after start date!";
  }
  return errors;
}

export const signTransactionMessage = async (
  messageText: string,
  wallet: WalletContextState,
  isLedger?: boolean
) => {
  if (!wallet) throw new Error("Wallet not connected!");

  if (isLedger) {
    const signature = await transferToSelf(wallet);
    return signature.toString();
  }

  if (!wallet.signMessage)
    throw new Error("Wallet does not support message signing!");

  const message = new TextEncoder().encode(messageText);
  const signedMessage = await wallet.signMessage(message);
  return encode(signedMessage);
};

//TODO@milica: move to new folder

export function mapUserSettingsFormValuesToUserAccountDto(
  values,
  wallet
): IUserAccount {
  return {
    discord: values.discord,
    email: values.email,
    imageBase64: values.userImage,
    telegram: values.telegram,
    instagram: values.instagram,
    linkedin: values.linkedin,
    name: values.username,
    pubKey: wallet!.publicKey.toString(),
    reddit: values.reddit,
    tiktok: values.tiktok,
    twitter: values.twitter,
    imageUrl: "",
    emailVerified: false,
    country: values.country,
    isLedger: values.isLedger,
    whitelisting: {
      areasOfInterest: values.areasOfInterest,
      domainOfExpertise: values.domainOfExpertise,
      personalBackground: values.personalBackground,
      interests: values.interests,
      status: WhitelistingStatus.Pending,
    },
  };
}

export function mapUserSettingsFromToWhitelistingDto(
  values,
  wallet: AnchorWallet,
  signMessage: string,
  id?: string
): ISaveWhitelistingDataDto {
  return {
    areasOfInterest: values.areasOfInterest.map((area) => area.label),
    domainOfExpertise: values.domainOfExpertise.map((domain) => domain.label),
    interests: values.monkeInterests,
    personalBackground: values.personalBackground,
    signMessage: signMessage,
    isLedger: values.isLedger,
    status: WhitelistingStatus.Pending,
    id,
    userPubkey: wallet.publicKey.toString(),
    user: {
      discord: values.discord,
      email: values.email,
      imageBase64: values.imageUpdated ? values.userImage : "",
      telegram: values.telegram,
      instagram: values.instagram,
      linkedin: values.linkedin,
      name: values.username,
      pubKey: wallet!.publicKey.toString(),
      reddit: values.reddit,
      tiktok: values.tiktok,
      twitter: values.twitter,
      imageUrl: "",
      emailVerified: false,
      country: values.country,
      signedMessage: signMessage,
      isLedger: values.isLedger,
    },
  };
}

export function uploadImage(
  acceptedFiles: any,
  reader: FileReader,
  fieldValue: string,
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void,
  userAccount?: boolean,
  editClub?: boolean
) {
  if (!acceptedFiles[0]) {
    createNotification(
      MESSAGE_TYPE.ERROR,
      "Can not upload too large image (2MB max)"
    );
    return;
  }
  reader.readAsDataURL(acceptedFiles[0]);
  reader.onload = async () => {
    const base64ImgFormat: any = reader.result;
    setFieldValue(fieldValue, base64ImgFormat);
    if (userAccount || editClub) {
      setFieldValue("imageUpdated", true);
      setFieldValue("removeImage", false);
    }
  };
}

export function mapUserSettingsFormValuesToUpdateUserAccountDto(
  values,
  wallet,
  signedMessage
): IUpdateUserAccountDto {
  return {
    discord: values.discord,
    email: values.email,
    imageBase64: values.imageUpdated ? values.userImage : "",
    telegram: values.telegram,
    instagram: values.instagram && values.instagram,
    linkedin: values.linkedin,
    name: values.username,
    reddit: values.reddit,
    tiktok: values.tiktok,
    twitter: values.twitter && values.twitter,
    removeImage: values.removeImage,
    country: values.country,
    signedMessage: signedMessage,
    isLedger: values.isLedger,
  };
}

export function mapUpdateClubFormValuesToUpdateClubDto(
  values: IUpdateClubFormValues,
  signedMessage: string
): IUpdateClubDto {
  return {
    discord: values.discord,
    email: values.clubEmail,
    imageBase64: values.imageUpdated ? values.clubImage : "",
    telegram: values.telegram,
    instagram: values.instagram && values.instagram,
    linkedin: values.linkedin,
    tags: values.clubTags,
    applicationStatus: values.clubApplicationStatus,
    description: values.clubDescription,
    reddit: values.reddit,
    tiktok: values.tiktok,
    twitter: values.twitter && values.twitter,
    removeImage: values.removeImage,
    signedMessage: signedMessage,
    depositAmount: values?.depositAmount,
  };
}

export function getSocialNetworkLinksAndIcons(values: any) {
  const socialLinkAndIcon: ISocialNetwork[] = [];

  Object.keys(values).map((key) => {
    if (SOCIAL_NETWORKS.includes(key) && values[key] !== EMPTY_STRING) {
      socialLinkAndIcon.push({
        socialNetworkIcon: SOCIAL_NETWORK_ICONS[key],
        socialNetworkUrl: values[key],
      });
    }
  });

  return socialLinkAndIcon;
}

export const validateClubName = async (clubName: string) => {
  const [realmAddress] = await PublicKey.findProgramAddress(
    [governanceSeed, Buffer.from(clubName)],
    splGovernanceProgram
  );
  const realmAccount = await RPC_CONNECTION.getAccountInfo(realmAddress);

  return !(realmAccount?.data && realmAccount.data.length > 0);
};

export const removeItem = (index, arrayHelpers) => {
  arrayHelpers.remove(index);
};

export function slugify(tags: IOption[]) {
  if (tags.length === 0) {
    return [];
  }
  let str = tags[tags.length - 1].value;
  str = str.replace(/^\s+|\s+$/g, "");
  str = str.toLowerCase();
  //TODO @goran:check if this is neccessary
  // var from =
  //   "ÁÄÂÀÃÅČÇĆĎÉĚËÈÊẼĔȆÍÌÎÏŇÑÓÖÒÔÕØŘŔŠŤÚŮÜÙÛÝŸŽáäâàãåčçćďéěëèêẽĕȇíìîïňñóöòôõøðřŕšťúůüùûýÿžþÞĐđßÆa·/_,:;";
  // var to =
  //   "AAAAAACCCDEEEEEEEEIIIINNOOOOOORRSTUUUUUYYZaaaaaacccdeeeeeeeeiiiinnooooooorrstuuuuuyyzbBDdBAa------";
  // for (var i = 0, l = from.length; i < l; i++) {
  //   str = str.replace(new RegExp(from.charAt(i), "g"), to.charAt(i));
  // }
  str = str
    .replace(/[^a-z0-9 -]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
  tags[tags.length - 1].value = str;
  return tags;
}

export const getMembersDetails = async (
  club: IClubBasicInfo
): Promise<IClubMember[]> => {
  try {
    const solanaMembers = await getClubMembers(club);
    const getUserDetailsDto: GetUserDetailsDto = {
      userAddresses: solanaMembers.map((item) => item.pubkey.toString()),
    };
    const membersFromApi = await getAllClubMembers(getUserDetailsDto);
    return solanaMembers.map((item) => {
      return {
        profileImageUrl: membersFromApi[item.pubkey.toString()]?.imageUrl || "",
        name: membersFromApi[item.pubkey.toString()]?.name || "",
        lastSeen: "2d",
        role: item.role,
        pubkey: item.pubkey.toString(),
        discord: membersFromApi[item.pubkey.toString()]?.discord || "",
        telegram: membersFromApi[item.pubkey.toString()]?.telegram || "",
        instagram: membersFromApi[item.pubkey.toString()]?.instagram || "",
        linkedin: membersFromApi[item.pubkey.toString()]?.linkedin || "",
        reddit: membersFromApi[item.pubkey.toString()]?.reddit || "",
        tiktok: membersFromApi[item.pubkey.toString()]?.tiktok || "",
        twitter: membersFromApi[item.pubkey.toString()]?.twitter || "",
      };
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export async function canCancel(
  proposal: IProposalWithMetaData,
  mint: PublicKey,
  realmName: string,
  user: AnchorWallet
) {
  try {
    const [realmPda] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );
    const [tokenOwnerRecord] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        realmPda.toBuffer(),
        mint.toBuffer(),
        user.publicKey.toBuffer(),
      ],
      splGovernanceProgram
    );

    return (
      tokenOwnerRecord.toString() ===
      proposal.proposalBasicData.proposalAccount.account.tokenOwnerRecord.toString()
    );
  } catch (error) {
    console.log(error);
  }
}

export const generateSkeletonArrays = (quantity: number) => [
  ...Array(quantity).keys(),
];

export const findFirstSubstring = (array: string[], queryParameter: string) => {
  for (let i = 0; i < array.length; i++) {
    if (array[i].indexOf(queryParameter) !== -1) return i;
  }
  return -1;
};

const throwTransactionErrorIfNeeded = (
  simulateResultLogs: string[],
  errorIndetifier: string,
  txid: string
) => {
  const hassIdentifiedError = findFirstSubstring(
    simulateResultLogs,
    errorIndetifier
  );

  if (hassIdentifiedError !== -1) {
    throw new TransactionError(
      simulateResultLogs[hassIdentifiedError].split(errorIndetifier)[1],
      txid
    );
  }
};

export const mapSolanaErrors = (
  simulateResult: SimulatedTransactionResponse | null,
  errorMessage: string,
  txid: string
) => {
  const errorIndetifiers = [
    TRANSACTION_ERROR_IDENTIFIER,
    TRANSFER_ERROR_IDENTIFIER,
    GOVERNANCE_ERROR_IDENTIFIER,
  ];

  if (simulateResult && simulateResult.err) {
    if (simulateResult.logs) {
      errorIndetifiers.forEach((item) => {
        throwTransactionErrorIfNeeded(simulateResult.logs!, item, txid);
      });
    }
  }

  throw new TransactionError(errorMessage, txid);
};

// TODO@vlada@goran: Check this method
export const checkSolAmount = (values, errors, fieldName: string) => {
  let digits;
  if (values.treasuryCap.toString().indexOf(".") > 0) {
    digits = values.treasuryCap.toString().split(".");
  } else {
    digits = values.treasuryCap.toString().split(",");
  }
  if (digits && digits.length === 2 && digits[1].length > values.mintDecimals) {
    errors[
      fieldName
    ] = `Treasury cap can not have more then ${values.mintDecimals} decimal places`;
  }
};

export function parseClubType(clubType: number) {
  switch (clubType) {
    case ClubType.RoleBased:
      return "Role-based";
    case ClubType.NftBased:
      return "Nft-based";
    case ClubType.TokenProportional:
      return "Deposit-based";
    case ClubType.ProjectFundraise:
      return "Project fundraise";
    default:
      return "No data";
  }
}

export function parseSecondsToDays(seconds: number | undefined) {
  if (!seconds) return undefined;
  dayjs.extend(duration);
  return dayjs.duration(seconds - 1, "seconds").asDays();
}
export const copyWalletAddress = async (pubKey: PublicKey) => {
  await navigator.clipboard.writeText(pubKey.toBase58());
  createNotification(MESSAGE_TYPE.SUCCESS, "Wallet address copied!");
};

export const toggleModalOpenClass = (state: boolean) => {
  document.body.classList.toggle("modal-open", state);
};

export const copyItemToClipboard = async (item: string, message: string) => {
  await navigator.clipboard.writeText(item);
  createNotification(MESSAGE_TYPE.SUCCESS, message);
};
export const getRaisedAmountFromLastActiveFundraise = (
  treasury: IClubTreasury
) => {
  return treasury?.fundraiseAccounts[treasury.fundraiseCount - 1].raisedAmount;
};
export const getCapAmountFromLastActiveFundraise = (
  treasury: IClubTreasury
) => {
  return treasury?.fundraiseAccounts[treasury.fundraiseCount - 1].capAmount;
};

export const isFundraiseInNoActiveWithNoPreviousStatus = (treasury) => {
  return (
    treasury &&
    treasury.treasuryFundraiseStatus ===
      TreasuryFundraiseStatus.NoActiveFundraiseWithPreviousFundraises
  );
};

export const validateAlphanumericString = (text: string) => {
  const regEx = new RegExp(`^[a-zA-Z0-9_ \.\'\-]+$`);
  return regEx.test(text);
};

export const validateTagValue = (text: string) => {
  const regEx = new RegExp(`^[a-zA-Z0-9_ ]+$`);
  return regEx.test(text);
};

//TODO@milica: add implementation
export const getAvailableWithdrawalAmount = () => {
  return 0.123456;
};

export const getTreasuryCount = (treasuryIndex: number) => {
  const treasuryBuffer = Buffer.alloc(4);
  treasuryBuffer.writeInt32LE(treasuryIndex + 1, 0);
  return treasuryBuffer;
};

export function round(num, decimalPlaces = 9) {
  var p = Math.pow(10, decimalPlaces);
  var n = num * p * (1 + Number.EPSILON);
  return Math.round(n) / p;
}

export function getApproximateValue(percentage: number): number | string {
  return percentage && percentage < 0.01 ? "<0.01" : percentage;
}

export const getMembersWithdrawalAmount = (
  withdrawalAmount: number,
  ownership: number
) => {
  return round(divide(multiply(ownership, withdrawalAmount), 100));
};

export const blockInvalidChar = (e) =>
  ["e", "E", "+", "-"].includes(e.key) && e.preventDefault();

export const getNftsFromRPC = async (
  pubKey: PublicKey,
  communityMint?: PublicKey
) => {
  try {
    const nftTokenAccounts = await getNftTokenAccountsByOwner(pubKey);
    const metadataAddressArr: PublicKey[] = [];
    const nfts: IDetailNftData[] = [];
    for (const nft of nftTokenAccounts) {
      metadataAddressArr.push(
        await Metadata.getPDA(new PublicKey(nft.account.data.parsed.info.mint))
      );
    }

    const metadaAccountChunk = chunk(metadataAddressArr, 100);
    for (const chunkItem of metadaAccountChunk) {
      const metadataAccountsRaw = await RPC_CONNECTION.getMultipleAccountsInfo(
        chunkItem
      );
      for (const metadataAccRaw of metadataAccountsRaw) {
        if (metadataAccRaw?.data) {
          const deserializedMetadata = MetadataData.deserialize(
            metadataAccRaw.data
          );
          //TODO@milica-monke-merge: Verified is removed for monke, check if we should remove this for main app too
          if (
            (communityMint &&
              deserializedMetadata.collection &&
              deserializedMetadata.collection.key ===
                communityMint.toBase58()) ||
            !communityMint
          ) {
            const tokenUriJson: any = {};
            try {
              tokenUriJson.json = await (
                await fetch(deserializedMetadata.data.uri)
              ).json();
            } catch (error) {
              console.log(error);
            }
            if (!tokenUriJson.json) {
              continue;
            }

            const [metadataAddress] = await PublicKey.findProgramAddress(
              [
                metadataSeed,
                METADATA_PROGRAM_ID.toBuffer(),
                new PublicKey(deserializedMetadata.mint).toBuffer(),
              ],
              METADATA_PROGRAM_ID
            );
            nfts.push({
              mint: new PublicKey(deserializedMetadata.mint),
              name: tokenUriJson.json.name,
              nftUrl: tokenUriJson.json.image,
              collection: {
                name: tokenUriJson.json.collection
                  ? (tokenUriJson.json.collection.name as string)
                  : EMPTY_STRING,
                key: deserializedMetadata.collection
                  ? deserializedMetadata.collection.key
                  : undefined,
              },

              description: tokenUriJson.description,
              owner: pubKey,
              metadataAddress: metadataAddress,
              tokenAccount: nftTokenAccounts.find(
                (account) =>
                  account.account.data.parsed.info.mint ===
                  deserializedMetadata.mint
              )?.pubkey,
            });
          }
        }
      }
    }
    return nfts;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

//TODO@milica-monke-merge: This is only for monke
// export const getMonkeNft = async (
//   pubKey: PublicKey
// ): Promise<PublicKey | undefined> => {
//   try {
//     const nftTokenAccounts = await getNftTokenAccountsByOwner(pubKey);
//     const metadataAddressArr: PublicKey[] = [];
//     for (const nft of nftTokenAccounts) {
//       metadataAddressArr.push(
//         await Metadata.getPDA(new PublicKey(nft.account.data.parsed.info.mint))
//       );
//     }

//     const metadaAccountChunk = chunk(metadataAddressArr, 100);
//     for (const chunkItem of metadaAccountChunk) {
//       const metadataAccountsRaw = await RPC_CONNECTION.getMultipleAccountsInfo(
//         chunkItem
//       );
//       for (const metadataAccRaw of metadataAccountsRaw) {
//         if (metadataAccRaw?.data) {
//           const deserializedMetadata = MetadataData.deserialize(
//             metadataAccRaw.data
//           );
//           if (
//             deserializedMetadata.collection &&
//             deserializedMetadata.collection.key === monkeMint.toString()
//           ) {
//             return new PublicKey(deserializedMetadata.mint);
//           }
//         }
//       }
//     }
//     return undefined;
//   } catch (error) {
//     console.log(error);
//     createNotification(
//       MESSAGE_TYPE.ERROR,
//       "Problem with loading monke nft from wallet"
//     );
//   }
// };

//TODO@milica-nfts: This is only for testing
// check if collection is verified is missing
export const getNftsWithQuickNode = async (
  pubkey: PublicKey,
  communityMint: PublicKey
): Promise<IDetailNftData[]> => {
  const responseArray: any[] = [];
  const response = await postQuickNode({
    jsonrpc: "2.0",
    id: 1,
    method: "qn_fetchNFTs",
    params: {
      wallet: pubkey.toString(),
      perPage: 40,
      page: 1,
    },
  });
  responseArray.push(response);
  const totalPages = response.result.totalPages;
  let pageNumber = response.result.pageNumber;
  while (totalPages > pageNumber) {
    const response = await postQuickNode({
      jsonrpc: "2.0",
      id: 1,
      method: "qn_fetchNFTs",
      params: {
        wallet: pubkey.toString(),
        perPage: 40,
        page: pageNumber + 1,
      },
    });
    responseArray.push(response);
    pageNumber = response.result.pageNumber;
  }
  const nfts: IDetailNftData[] = [];
  for (const response of responseArray) {
    response.result.assets
      .filter((item) => item.collectionAddress === communityMint.toString())
      .forEach((element) => {
        nfts.push({
          mint: new PublicKey(element.tokenAddress),
          name: element.name,
          nftUrl: element.imageUrl,
          collection: {
            name: element.collectionName,
            key: element.collectionAddress,
          },
          description: element.description,
          owner: pubkey,
        });
      });
  }

  return nfts;
};

export const getBasicInfoForClubInvitationsAndMembers = async (
  club: IClubBasicInfo
): Promise<IClubMemberAndInvitation[]> => {
  try {
    const solanaMembers = await getClubMembersAndInvitations(club);
    const getUserDetailsDto: GetUserDetailsDto = {
      userAddresses: solanaMembers.map((item) => item.pubkey.toString()),
    };
    const membersFromApi = await getAllClubMembers(getUserDetailsDto);
    return solanaMembers.map((item) => {
      return {
        name: membersFromApi[item.pubkey.toString()]?.name,
        role: item.role,
        pubkey: item.pubkey.toString(),
        imageUrl: membersFromApi[item.pubkey.toString()]?.imageUrl,
        onlyInvited: !item.isAccepted,
      };
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getAllocationModel = (
  treasury: IClubTreasury,
  wallet?: AnchorWallet
) => {
  const lastFundraise =
    treasury?.fundraiseAccounts[treasury.fundraiseCount - 1];
  if (
    lastFundraise.allocation?.custom &&
    lastFundraise.allocation?.custom.find(
      (el) => el.authority.toString() === wallet?.publicKey.toString()
    )?.amount
  ) {
    return {
      model: "Custom",
      value: `(${
        lastFundraise.allocation?.custom &&
        lastFundraise.allocation?.custom.find(
          (el) => el.authority.toString() === wallet?.publicKey.toString()
        )?.amount
      } ${getTreasurySymbol(treasury!.denominatedTokenInfo)})`,
    };
  } else if (lastFundraise.allocation?.equal) {
    return {
      model: "Limited",
      value: `(${lastFundraise.allocation?.equal} ${getTreasurySymbol(
        treasury!.denominatedTokenInfo
      )})`,
    };
  }
  return {
    model: "Unlimited",
  };
};

export const getDecimalsPerMint = (
  denominatedTokenInfo: ITreasuryTokenAccInfo | undefined
) => {
  return denominatedTokenInfo
    ? Number(pow(10, denominatedTokenInfo?.decimals))
    : LAMPORTS_PER_SOL;
};

export const getMaxDecimalsLengthPerMint = (
  denominatedTokenInfoDecimals: number | undefined
) => {
  return denominatedTokenInfoDecimals
    ? 1 / Number(pow(10, denominatedTokenInfoDecimals))
    : 1 / LAMPORTS_PER_SOL;
};

export const getTreasurySymbol = (
  denominatedTokenInfo: ITreasuryTokenAccInfo | undefined
) => {
  return denominatedTokenInfo?.symbol ?? "SOL";
};

export const uniqByKeepFirst = (a, key) => {
  let seen = new Set();
  return a.filter((item) => {
    let k = key(item);
    return seen.has(k) ? false : seen.add(k);
  });
};

export const getMintDecimalsDivider = (decimals: number) => {
  return Number(pow(10, decimals));
};

export const arrayToDict = (array: any[], key: string) =>
  Object.assign({}, ...array.map((x) => ({ [x[key]]: { ...x } })));

export const arrDifference = (arrA: any[], arrB?: any[]) => {
  if (!arrB) return arrA;
  return arrA.filter(
    (item) =>
      !arrB.some((itemToBeRemoved) => itemToBeRemoved.wallet === item.wallet)
  );
};
